import { apiRequest, apiUrl } from "./http";
import type { AdoptedCountResponse, AttachInvoicePayload, DeletedCountResponse, Invoice, InvoicePayload, InvoiceReview, ProjectInvoices, ReleasedCountResponse, RestoredCostsResponse, UpdateInvoicePayload } from "./generated/openapi";
export type { Invoice, InvoiceLine, InvoicePayload, InvoiceStatus, LineAmountTypes, ProjectInvoices, UnbilledCost } from "./generated/openapi";

export const invoiceApi = {
  forProject: (projectId: string | number) =>
    apiRequest<ProjectInvoices>(`/api/projects/${projectId}/invoices`),

  create: (projectId: string | number, payload: InvoicePayload) =>
    apiRequest<Invoice>(`/api/projects/${projectId}/invoices`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  /** Links an invoice raised directly in Xero, found by its number. */
  attach: (projectId: string | number, invoiceNumber: string) =>
    apiRequest<Invoice>(`/api/projects/${projectId}/invoices/attach`, {
      method: "POST",
      body: JSON.stringify({ invoice_number: invoiceNumber } satisfies AttachInvoicePayload),
    }),

  get: (invoiceId: number) => apiRequest<Invoice>(`/api/invoices/${invoiceId}`),

  update: (
    invoiceId: number,
    payload: UpdateInvoicePayload,
  ) =>
    apiRequest<Invoice>(`/api/invoices/${invoiceId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),

  remove: (invoiceId: number) =>
    apiRequest<DeletedCountResponse>(`/api/invoices/${invoiceId}`, {
      method: "DELETE",
    }),

  issue: (invoiceId: number) =>
    apiRequest<Invoice>(`/api/invoices/${invoiceId}/issue`, { method: "POST" }),

  /** What the portal holds against what Xero currently bills. */
  review: (invoiceId: number) =>
    apiRequest<InvoiceReview>(`/api/invoices/${invoiceId}/review`),

  /** Records lines added directly in Xero as costs on the project. */
  adoptLines: (invoiceId: number) =>
    apiRequest<AdoptedCountResponse>(`/api/invoices/${invoiceId}/adopt-lines`, {
      method: "POST",
    }),

  restoreCosts: (invoiceId: number) =>
    apiRequest<RestoredCostsResponse>(
      `/api/invoices/${invoiceId}/restore-costs`,
      { method: "POST" },
    ),

  releaseCosts: (invoiceId: number) =>
    apiRequest<ReleasedCountResponse>(`/api/invoices/${invoiceId}/release-costs`, {
      method: "POST",
    }),

  refresh: (invoiceId: number) =>
    apiRequest<Invoice>(`/api/invoices/${invoiceId}/refresh`, { method: "POST" }),

  /**
   * Xero's own rendered PDF, streamed through the API. The portal renders
   * nothing itself, so this is byte-for-byte what Xero would send a client.
   */
  pdfUrl: (invoiceId: number) => apiUrl(`/api/invoices/${invoiceId}/pdf`),
};
